import { Grid2, styled } from '@mui/material'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import React from 'react'
import { useSelector } from 'react-redux'

import CampaignLink from '@/app/component/atom/campaign-link'
import Tooltip from '@/app/component/atom/tooltip'
import { noop } from '@/app/definitions'
import SendMessageDialog, { SendMessageDialogProps } from '@/app/module/common/component/send-message-dialog'
import { STATUS_TOOLTIP } from '@/app/module/logs/definitions'
import { formatCost } from '@/app/module/logs/helpers'
import { getSMSMessageStatus } from '@/app/module/logs/message-status'
import { selectPersonalizationList } from '@/app/module/logs/store/selectors'
import { getContactNumber, getSenderReceiverNumber, isInbound } from '@/app/module/logs/utils'
import { dateTime } from '@/app/service/util'
import { RootState } from '@/app/store'
import { DirectionType } from '@/app/types'

import { DataCell, RowContainer, Truncate } from './common'
import ContactIcon from './contact-icon'
import ContactPhoneDisplay from './contact-phone-display'

type Props = {
  campaignName?: string
  chargedMicro: number
  contactDeleted: boolean
  contactId: number
  created: string
  deliveredToPhone: string
  deliveredToTelco: boolean
  direction: DirectionType
  done: boolean
  engagementFlowId?: number
  message: string
  pricePerPart?: number
  recipient: string
  sender: string
  successPartCount?: number
  windowWidth: number
  viewContact: (contactId: number) => void
}

const defaultSendMessageProps: SendMessageDialogProps['sendMessageProps'] = {
  contactIds: [],
  recipients: [],
  totalRecipients: 0,
  excludeIds: [],
  isAllContacts: false,
  includedGroupIds: [],
  search: '',
}

const MessageRow: React.FC<Props> = (props) => {
  const personalizationList = useSelector((state: RootState) =>
    selectPersonalizationList({ customFields: state.customFields }),
  )
  const [popperOpen, setPopperOpen] = React.useState(false)
  const [sendMessageModalOpen, setSendMessageModalOpen] = React.useState(false)
  const [sendMessageProps, setSendMessageProps] =
    React.useState<SendMessageDialogProps['sendMessageProps']>(defaultSendMessageProps)

  const truncateMessageLength = React.useCallback(() => {
    const { windowWidth } = props
    if (windowWidth > 1080) {
      return 80
    }
    return (windowWidth - 100) / 12
  }, [props])

  const handleSendMessage = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()

    const { contactId } = props
    const recipient = getContactNumber(props)

    setSendMessageProps({
      contactIds: [contactId],
      recipients: [{ label: recipient, value: recipient }],
      totalRecipients: 1,
      excludeIds: [],
      includedGroupIds: [],
      isAllContacts: false,
      search: '',
    })
    setSendMessageModalOpen(true)
  }

  const { campaignName, chargedMicro, contactDeleted, contactId, created, engagementFlowId, message, viewContact } =
    props

  const isInboundType = isInbound(props)
  const contactNumber = getContactNumber(props)
  const messageStatus = getSMSMessageStatus(props)
  const rowSenderId = getSenderReceiverNumber(props)

  return (
    <RowContainer>
      <CardContent>
        <Grid2 container>
          <Grid2 size={{ xs: 12, sm: 3, md: 3 }}>
            <ContactPhoneDisplay direction={isInboundType ? 'inbound' : 'outbound'} phoneNumber={contactNumber} />
          </Grid2>
          <DataCell size={{ xs: 3, sm: 2, md: 2 }}>
            <Typography color="textSecondary" align="center">
              <Tooltip title={STATUS_TOOLTIP[messageStatus]}>
                <span>{messageStatus}</span>
              </Tooltip>
            </Typography>
            <Typography color="primary" align="center" variant="body1">
              <b>({formatCost(chargedMicro)})</b>
            </Typography>
          </DataCell>
          <DataCell size={{ xs: 3, sm: 2, md: 2 }}>
            <Typography color="textSecondary" align="center">
              <Tooltip title={'Date and Time'}>
                <span>&nbsp;{dateTime(created)}</span>
              </Tooltip>
            </Typography>
          </DataCell>
          <DataCell size={{ xs: 2, sm: 2, md: 2 }}>
            <CampaignLink align="center" campaignId={engagementFlowId} campaignName={campaignName} />
          </DataCell>
          <DataCell size={{ xs: 2, sm: 2, md: 2 }}>
            <Typography color="textSecondary" align="center">
              <Tooltip title={'From/To'}>
                <span>{getSenderReceiverNumber(props)}</span>
              </Tooltip>
            </Typography>
          </DataCell>
          <Grid2 size={{ xs: 2, sm: 1, md: 1 }}>
            {!contactDeleted && !!contactId && (
              <Tooltip title={'Send Message'}>
                <IconButton aria-label="Send Message" onClick={handleSendMessage}>
                  <Icon>message</Icon>
                </IconButton>
              </Tooltip>
            )}
            <ContactIcon contactDeleted={contactDeleted} contactId={contactId} onClick={viewContact} />
          </Grid2>
        </Grid2>
        <Grid2 container>
          <Grid2 size={{ xs: 12 }} onMouseOver={() => setPopperOpen(true)} onMouseOut={() => setPopperOpen(false)}>
            <Truncate variant="subtitle1" color="textPrimary">
              {message}
            </Truncate>
            {message && message.length > truncateMessageLength() && (
              <Popover
                style={{ display: popperOpen ? 'block' : 'none' }}
                id="react-popper-tooltip"
                role="tooltip"
                aria-hidden={!popperOpen}
                elevation={8}
              >
                <StyledCardContent>
                  <Typography variant="subtitle1" color="textPrimary">
                    {message}
                  </Typography>
                </StyledCardContent>
              </Popover>
            )}
          </Grid2>
        </Grid2>
      </CardContent>
      <SendMessageDialog
        isOpen={sendMessageModalOpen}
        personalizationList={personalizationList}
        sendMessageProps={sendMessageProps}
        senderId={{ value: rowSenderId }}
        onClearSelection={noop}
        onClose={() => setSendMessageModalOpen(false)}
      />
    </RowContainer>
  )
}

export default MessageRow

const Popover = styled(Card)(() => ({
  position: 'absolute',
  marginRight: '20px',
  top: '50px',
  left: '27px',
  zIndex: '1',
}))

const StyledCardContent = styled(CardContent)(() => ({
  padding: '15px',
}))
